import {Button, Form, Input, Typography, Upload, Spin, message, Modal, Select} from "antd";
import React, { useEffect, useState } from "react";
import { getLLMPrice } from "../../utils/api";
import { 
    InboxOutlined, 
    CheckCircleTwoTone, 
    DeleteOutlined, 
    DollarOutlined, 
    InfoCircleOutlined, 
    TagOutlined, 
    ExpandOutlined, 
    ClockCircleOutlined,
    RobotOutlined
} from "@ant-design/icons";
import { UploadFile, UploadChangeParam } from 'antd/es/upload/interface';
import { getCategoryLabel } from "../../utils/helpers";
import { analytics } from "../../firebase";
import { logEvent } from "firebase/analytics";

const { Title, Text } = Typography;

const username = "user_jt7bdy";
const model = "claude-haiku-4-5";

const EstimateListingPage: React.FC = () => {
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState<any[]>([]);
    const [description, setDescription] = useState("");
    const [estimatedAmount, setEstimatedAmount] = useState<number | null>(null);
    const [priceExplanation, setPriceExplanation] = useState<string>("");
    const [categoryId, setCategoryId] = useState<string>("");
    const [size, setSize] = useState("");
    const [responseTime, setResponseTime] = useState<number | null>(null);
    const [isEstimating, setIsEstimating] = useState<boolean>(false);
    const [showEstimate, setShowEstimate] = useState<boolean>(false);
    const [, setSuccessAnim] = useState<boolean>(false);
    const [dragIconAnim, setDragIconAnim] = useState<boolean>(false);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [listingType, setListingType] = useState<'product' | 'service'>('product');
    const isService = listingType === 'service';

    const label = getCategoryLabel(categoryId);

  // GA4 page view
  useEffect(() => {
    try {
      if (analytics) {
        logEvent(analytics as any, 'page_view', {
          page_title: 'AI Estimate',
          page_location: window.location.href,
          page_path: window.location.pathname || '/estimate-listing',
          app_platform: 'web'
        });
      }
    } catch (_) {}
  }, []);

    const onImageChange = (info: UploadChangeParam<UploadFile<any>>) => {
        const latestFiles = info.fileList.slice(-1);
        setFileList(latestFiles);
        if (latestFiles.length && latestFiles[0].originFileObj) {
            const reader = new FileReader();
            reader.onload = e => setImagePreview(e.target?.result as string);
            reader.readAsDataURL(latestFiles[0].originFileObj);
        }
        setDragIconAnim(true);
        setTimeout(() => setDragIconAnim(false), 800);
    };

    const removeImage = () => {
        setFileList([]);
        setImagePreview(null);
    }

    const fetchEstimate = async () => {
        if (!isService && fileList.length === 0) {
            message.warning("Please upload an image");
            return;
        }
        if (description.trim() === '') {
            message.warning("Please enter a description");
            return;
        }
        setIsEstimating(true);
        setShowEstimate(false);
        try {
            let priceResult;
            if (isService) {
                priceResult = await getLLMPrice([], description, username, model, { isService: true });
            } else {
                const fileToDataURL = (file: File): Promise<string> => {
                    return new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result as string);
                        reader.onerror = reject;
                        reader.readAsDataURL(file);
                    });
                };
                const fileObj = fileList[0].originFileObj;
                const dataURL = await fileToDataURL(fileObj);
                priceResult = await getLLMPrice([dataURL], description, username, model);
            }

            const llmPrice = priceResult.listing.llmPrice;
            setEstimatedAmount(llmPrice);
            setPriceExplanation(priceResult.listing.priceExplanation);
            setCategoryId(priceResult.listing.categoryId);
            setSize(priceResult.listing.size);
            setResponseTime(priceResult.responseTime);
            setSuccessAnim(true);
            setTimeout(() => setSuccessAnim(false), 1400);
            setShowEstimate(true);
        } catch (e) {
            console.error("Estimation error:", e);
        } finally {
            setIsEstimating(false);
        }
    };

    // Brand colors matching other pages
    const brandColors = {
        dark: '#222831',
        red: '#e83446', 
        light: '#eeeeee'
    };

    return (
        <div style={{
            minHeight: "100vh",
            background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
            position: "relative",
            display: "flex",
            flexDirection: "column"
        }}>
            {/* Background Pattern */}
            <div style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `
                    radial-gradient(circle at 20% 20%, rgba(102, 126, 234, 0.05) 0%, transparent 50%),
                    radial-gradient(circle at 80% 80%, rgba(252, 92, 101, 0.05) 0%, transparent 50%),
                    radial-gradient(circle at 40% 60%, rgba(16, 185, 129, 0.03) 0%, transparent 50%)
                `,
                zIndex: 0
            }} />

            <div style={{
                background: "transparent",
                flex: 1,
                padding: window.innerWidth < 768 ? "16px" : "32px",
                position: "relative",
                zIndex: 1,
                display: "flex",
                flexDirection: window.innerWidth < 768 ? "column" : "row",
                gap: window.innerWidth < 768 ? "24px" : "32px",
                maxWidth: "1600px",
                margin: "0 auto",
                width: "100%"
            }}>
                {/* Left Side - Form */}
                <div style={{
                    flex: 1,
                    background: "rgba(255, 255, 255, 0.8)",
                    backdropFilter: "blur(20px)",
                    borderRadius: window.innerWidth < 768 ? "24px" : "32px",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.08)",
                    border: "1px solid rgba(255,255,255,0.3)",
                    padding: window.innerWidth < 768 ? "24px 20px" : "48px 40px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    minHeight: window.innerWidth < 768 ? "auto" : "80vh"
                }}>
                    {/* Premium Header */}
                    <div style={{
                        textAlign: "center",
                        marginBottom: "40px",
                        position: "relative"
                    }}>
                        <div style={{
                            position: "absolute",
                            top: "-20px",
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: "120px",
                            height: "120px",
                            background: `radial-gradient(circle, ${brandColors.red}10 0%, transparent 70%)`,
                            borderRadius: "50%",
                            zIndex: 0
                        }} />
                        
                        <div style={{ position: "relative", zIndex: 1 }}>
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "16px",
                                marginBottom: "16px"
                            }}>
                                <div style={{
                                    width: "48px",
                                    height: "48px",
                                    borderRadius: "16px",
                                    background: `linear-gradient(135deg, ${brandColors.red} 0%, #ff6b6b 100%)`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: `0 8px 20px ${brandColors.red}30`
                                }}>
                                    <RobotOutlined style={{ color: "#ffffff", fontSize: "20px" }} />
                                </div>
                                <Title level={1} style={{
                                    fontSize: "42px",
                                    fontWeight: 800,
                                    color: brandColors.dark,
                                    margin: 0,
                                    background: `linear-gradient(135deg, ${brandColors.dark} 0%, ${brandColors.red} 100%)`,
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    backgroundClip: "text"
                                }}>
                                    AI Price Estimate
                    </Title>
                            </div>
                            
                            <Text style={{
                                fontSize: "18px",
                                color: "#64748b",
                                fontWeight: 500,
                                maxWidth: "500px",
                                margin: "0 auto",
                                lineHeight: "1.6"
                            }}>
                                Get an instant, AI-powered price estimate for your items. Upload an image and description to get started.
                            </Text>
                        </div>
                    </div>

                    <Form form={form} layout="vertical" onFinish={fetchEstimate}>
                        <Form.Item label={
                            <Text style={{ fontSize: "16px", fontWeight: 600, color: brandColors.dark }}>
                                Type
                            </Text>
                        } style={{ marginBottom: "24px" }}>
                            <Select
                                value={listingType}
                                onChange={(val) => {
                                    setListingType(val);
                                    setFileList([]);
                                    setImagePreview(null);
                                }}
                                style={{ width: "100%", borderRadius: "16px" }}
                                size="large"
                                options={[
                                    { value: 'product', label: 'Product' },
                                    { value: 'service', label: 'Service' },
                                ]}
                            />
                        </Form.Item>

                        {!isService && <Form.Item label={
                            <Text style={{ fontSize: "16px", fontWeight: 600, color: brandColors.dark }}>
                                Upload Item Image
                            </Text>
                        } required style={{ marginBottom: "24px" }}>
                            {!imagePreview ? (
                                <Upload.Dragger
                                    beforeUpload={() => false}
                                    maxCount={1}
                                    accept="image/*"
                                    fileList={fileList}
                                    onChange={onImageChange}
                                    onRemove={removeImage}
                                    listType="picture"
                                    style={{
                                        borderRadius: "20px",
                                        background: "rgba(255, 255, 255, 0.8)",
                                        border: `2px dashed ${brandColors.red}40`,
                                        minHeight: "120px",
                                        transition: "all 0.3s ease"
                                    }}
                                >
                                    <div style={{ textAlign: 'center' }}>
                                        <InboxOutlined
                                            style={{
                                                fontSize: "48px",
                                                color: brandColors.red,
                                                transition: "transform 0.4s, opacity 0.4s",
                                                transform: dragIconAnim ? "rotate(-15deg) scale(1.2)" : "none",
                                                opacity: dragIconAnim ? 0.7 : 1
                                            }}
                                        />
                                    </div>
                                    <Text style={{ 
                                        fontWeight: 600, 
                                        marginBottom: "8px", 
                                        fontSize: "16px",
                                        color: brandColors.dark,
                                        display: "block"
                                    }}>
                                        Click or drag image to upload
                                    </Text>
                                    <Text style={{ 
                                        fontSize: "14px", 
                                        color: "#64748b",
                                        display: "block"
                                    }}>
                                        Only one image needed for estimate
                                    </Text>
                                </Upload.Dragger>
                            ) : (
                                <div style={{textAlign: 'center'}}>
                                    <div style={{
                                        position: "relative",
                                        display: "inline-block",
                                        borderRadius: "20px",
                                        overflow: "hidden",
                                        boxShadow: "0 12px 32px rgba(0,0,0,0.15)",
                                        border: `3px solid ${brandColors.red}20`
                                    }}>
                                        <img 
                                            src={imagePreview} 
                                            alt={"Preview"} 
                                            style={{ 
                                                width: "100%", 
                                                maxWidth: "400px",
                                                height: "auto",
                                                borderRadius: "16px", 
                                                cursor: "pointer",
                                                transition: "transform 0.3s ease"
                                            }}
                                            onClick={()=>setShowModal(true)}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = "scale(1.02)";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = "scale(1)";
                                            }}
                                        />
                                        <div style={{
                                            position: "absolute",
                                            top: "12px",
                                            right: "12px",
                                            background: "rgba(255, 255, 255, 0.9)",
                                            borderRadius: "50%",
                                            width: "32px",
                                            height: "32px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: "pointer",
                                            backdropFilter: "blur(10px)"
                                        }}
                                        onClick={()=>setShowModal(true)}
                                        >
                                            <ExpandOutlined style={{ color: brandColors.red, fontSize: "16px" }} />
                                        </div>
                                    </div>
                                    
                                    <Modal 
                                        open={showModal} 
                                        footer={null} 
                                        onCancel={() => setShowModal(false)} 
                                        centered 
                                        width={1400}
                                        style={{ top: 20 }}
                                    >
                                        <img src={imagePreview} alt="Full Preview" style={{ width: '100%', height: 'auto', maxHeight: '75vh', objectFit: 'contain', borderRadius: "12px" }} />
                                    </Modal>

                                    <Button 
                                        icon={<DeleteOutlined />} 
                                        onClick={removeImage} 
                                        danger 
                                        size="middle" 
                                        style={{ 
                                            marginTop: "16px",
                                            borderRadius: "12px",
                                            height: "40px",
                                            fontWeight: 600
                                        }}
                                    >
                                        Remove Image
                                    </Button>
                                </div>
                            )}
                        </Form.Item>}

                        <Form.Item label={
                            <Text style={{ fontSize: "16px", fontWeight: 600, color: brandColors.dark }}>
                                Description
                            </Text>
                        } required>
                            <Input.TextArea
                                rows={4}
                                placeholder={isService ? "Describe your service, e.g. 30-min haircut, location, experience, etc." : "Describe your item, condition, brand, price, etc."}
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                style={{ 
                                    borderRadius: "16px",
                                    border: `2px solid rgba(0,0,0,0.1)`,
                                    fontSize: "16px",
                                    padding: "16px",
                                    transition: "all 0.3s ease"
                                }}
                            />
                        </Form.Item>

                        <div style={{ textAlign: 'center', marginTop: "24px" }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={isEstimating}
                                style={{
                                    width: "200px",
                                    height: "56px",
                                    fontSize: "18px",
                                    borderRadius: "16px",
                                    fontWeight: 700,
                                    background: `linear-gradient(135deg, ${brandColors.red} 0%, #ff6b6b 100%)`,
                                    border: "none",
                                    boxShadow: `0 8px 24px ${brandColors.red}40`,
                                    transition: "all 0.3s ease"
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = "translateY(-2px)";
                                    e.currentTarget.style.boxShadow = `0 12px 32px ${brandColors.red}50`;
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = "translateY(0)";
                                    e.currentTarget.style.boxShadow = `0 8px 24px ${brandColors.red}40`;
                                }}
                            >
                                {isEstimating ? "Analyzing..." : "Estimate Price"}
                            </Button>
                        </div>
                    </Form>

                    {isEstimating && (
                        <div style={{ 
                            textAlign: 'center', 
                            marginTop: "32px",
                            padding: "24px",
                            background: "rgba(255, 255, 255, 0.6)",
                            borderRadius: "20px",
                            border: `1px solid ${brandColors.red}20`
                        }}>
                            <Spin size="large" />
                            <div style={{ marginTop: "16px" }}>
                                <Text style={{ 
                                    fontSize: "16px", 
                                    color: brandColors.dark, 
                                    fontWeight: 600 
                                }}>
                                    AI is analyzing your item...
                                </Text>
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Side - AI Output */}
                <div style={{
                    flex: 1,
                    background: "rgba(255, 255, 255, 0.8)",
                    backdropFilter: "blur(20px)",
                    borderRadius: window.innerWidth < 768 ? "24px" : "32px",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.08)",
                    border: "1px solid rgba(255,255,255,0.3)",
                    padding: window.innerWidth < 768 ? "24px 20px" : "48px 40px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    minHeight: "80vh",
                    position: "relative"
                }}>
                    {!showEstimate ? (
                        <div style={{
                            textAlign: "center",
                            padding: "60px 20px"
                        }}>
                            <div style={{
                                width: "120px",
                                height: "120px",
                                borderRadius: "50%",
                                background: `linear-gradient(135deg, ${brandColors.red}10 0%, ${brandColors.red}20 100%)`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                margin: "0 auto 32px",
                                boxShadow: `0 20px 40px ${brandColors.red}20`
                            }}>
                                <RobotOutlined style={{ color: brandColors.red, fontSize: "48px" }} />
                            </div>
                            
                            <Title level={3} style={{
                                color: brandColors.dark,
                                marginBottom: "16px",
                                fontSize: "28px",
                                fontWeight: 700
                            }}>
                                AI Analysis Ready
                            </Title>
                            
                            <Text style={{
                                color: "#64748b",
                                fontSize: "16px",
                                lineHeight: "1.6",
                                maxWidth: "400px",
                                margin: "0 auto",
                                display: "block"
                            }}>
                                Upload an image and description to get an instant AI-powered price estimate with detailed analysis.
                            </Text>
                        </div>
                    ) : (
                        <div style={{ width: "100%" }}>
                            {/* Premium Header */}
                            <div style={{
                                textAlign: "center",
                                marginBottom: "32px",
                                position: "relative"
                            }}>
                                <div style={{
                                    position: "absolute",
                                    top: "-20px",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    width: "100px",
                                    height: "100px",
                                    background: `radial-gradient(circle, ${brandColors.red}10 0%, transparent 70%)`,
                                    borderRadius: "50%",
                                    zIndex: 0
                                }} />
                                
                                <div style={{ position: "relative", zIndex: 1 }}>
                                    <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "12px",
                                        marginBottom: "12px"
                                    }}>
                                        <CheckCircleTwoTone 
                                            twoToneColor={brandColors.red} 
                                            style={{ fontSize: "24px" }} 
                                        />
                                        <Text style={{
                                            fontSize: "18px",
                                            fontWeight: 600,
                                            color: "#10b981"
                                        }}>
                                            Analysis Complete
                                        </Text>
                                    </div>
                                    
                                    <Title level={2} style={{
                                        fontSize: "36px",
                                        fontWeight: 800,
                                        color: brandColors.dark,
                                        margin: 0,
                                        background: `linear-gradient(135deg, ${brandColors.dark} 0%, ${brandColors.red} 100%)`,
                                        WebkitBackgroundClip: "text",
                                        WebkitTextFillColor: "transparent",
                                        backgroundClip: "text"
                                    }}>
                                        ${estimatedAmount ?? "N/A"}
                                </Title>
                                    
                                    <Text style={{
                                        fontSize: "16px",
                                        color: "#64748b",
                                        fontWeight: 500
                                    }}>
                                        Estimated Market Value
                                    </Text>
                                </div>
                            </div>

                            {/* Premium Results Cards */}
                            <div style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "20px"
                            }}>
                                {/* Price Card */}
                                <div style={{
                                    background: `linear-gradient(135deg, ${brandColors.red}05 0%, ${brandColors.red}10 100%)`,
                                    borderRadius: "20px",
                                    padding: "24px",
                                    border: `1px solid ${brandColors.red}20`,
                                    position: "relative",
                                    overflow: "hidden"
                                }}>
                                    <div style={{
                                        position: "absolute",
                                        top: "-20px",
                                        right: "-20px",
                                        width: "80px",
                                        height: "80px",
                                        background: `radial-gradient(circle, ${brandColors.red}20 0%, transparent 70%)`,
                                        borderRadius: "50%"
                                    }} />
                                    
                                    <div style={{ position: "relative", zIndex: 1 }}>
                                        <div style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "12px",
                                            marginBottom: "12px"
                                        }}>
                                            <DollarOutlined style={{ 
                                                color: brandColors.red, 
                                                fontSize: "20px" 
                                            }} />
                                            <Text style={{
                                                fontSize: "16px",
                                                fontWeight: 600,
                                                color: brandColors.dark
                                            }}>
                                                Estimated Price
                                            </Text>
                                        </div>
                                        
                                        <Text style={{
                                            fontSize: "32px",
                                            fontWeight: 800,
                                            color: brandColors.red,
                                            margin: 0
                                        }}>
                                            ${estimatedAmount ?? "N/A"}
                                        </Text>
                                    </div>
                                </div>

                                {/* Explanation Card */}
                                <div style={{
                                    background: "rgba(255, 255, 255, 0.6)",
                                    borderRadius: "20px",
                                    padding: "24px",
                                    border: "1px solid rgba(0,0,0,0.05)",
                                    backdropFilter: "blur(10px)"
                                }}>
                                    <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "12px",
                                        marginBottom: "16px"
                                    }}>
                                        <InfoCircleOutlined style={{ 
                                            color: brandColors.red, 
                                            fontSize: "20px" 
                                        }} />
                                        <Text style={{
                                            fontSize: "16px",
                                            fontWeight: 600,
                                            color: brandColors.dark
                                        }}>
                                            AI Explanation
                                        </Text>
                                    </div>
                                    
                                    <Text style={{
                                        fontSize: "15px",
                                        color: "#64748b",
                                        lineHeight: "1.6",
                                        margin: 0
                                    }}>
                                        {priceExplanation ?? "N/A"}
                                    </Text>
                                </div>

                                {/* Details Row */}
                                <div style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "16px"
                                }}>
                                    {/* Category */}
                                    <div style={{
                                        background: "rgba(255, 255, 255, 0.6)",
                                        borderRadius: "16px",
                                        padding: "20px",
                                        border: "1px solid rgba(0,0,0,0.05)",
                                        backdropFilter: "blur(10px)",
                                        textAlign: "center"
                                    }}>
                                        <TagOutlined style={{ 
                                            color: brandColors.red, 
                                            fontSize: "20px",
                                            marginBottom: "8px",
                                            display: "block"
                                        }} />
                                        <Text style={{
                                            fontSize: "14px",
                                            fontWeight: 600,
                                            color: "#64748b",
                                            display: "block",
                                            marginBottom: "4px"
                                        }}>
                                            Category
                                        </Text>
                                        <Text style={{
                                            fontSize: "16px",
                                            fontWeight: 700,
                                            color: brandColors.dark,
                                            margin: 0
                                        }}>
                                            {label ?? categoryId ?? "N/A"}
                                        </Text>
                                    </div>

                                    {/* Size */}
                                    <div style={{
                                        background: "rgba(255, 255, 255, 0.6)",
                                        borderRadius: "16px",
                                        padding: "20px",
                                        border: "1px solid rgba(0,0,0,0.05)",
                                        backdropFilter: "blur(10px)",
                                        textAlign: "center"
                                    }}>
                                        <ExpandOutlined style={{ 
                                            color: brandColors.red, 
                                            fontSize: "20px",
                                            marginBottom: "8px",
                                            display: "block"
                                        }} />
                                        <Text style={{
                                            fontSize: "14px",
                                            fontWeight: 600,
                                            color: "#64748b",
                                            display: "block",
                                            marginBottom: "4px"
                                        }}>
                                            Size
                                        </Text>
                                        <Text style={{
                                            fontSize: "16px",
                                            fontWeight: 700,
                                            color: brandColors.dark,
                                            margin: 0
                                        }}>
                                            {size ?? "N/A"}
                                        </Text>
                                    </div>
                                </div>

                                {/* Response Time */}
                                <div style={{
                                    background: "rgba(255, 255, 255, 0.4)",
                                    borderRadius: "16px",
                                    padding: "16px 20px",
                                    border: "1px solid rgba(0,0,0,0.05)",
                                    backdropFilter: "blur(10px)",
                                    textAlign: "center"
                                }}>
                                    <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "8px"
                                    }}>
                                        <ClockCircleOutlined style={{ 
                                            color: "#64748b", 
                                            fontSize: "16px" 
                                        }} />
                                        <Text style={{
                                            fontSize: "14px",
                                            color: "#64748b",
                                            fontWeight: 500
                                        }}>
                                            Response Time: {responseTime}ms
                                        </Text>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div style={{
                                marginTop: "32px",
                                display: "flex",
                                gap: "12px",
                                justifyContent: "center"
                            }}>
                                <Button
                                    size="large"
                                    style={{
                                        color: brandColors.red,
                                        border: `2px solid ${brandColors.red}`,
                                        borderRadius: "12px",
                                        height: "48px",
                                        fontWeight: 600,
                                        padding: "0 24px",
                                        background: "transparent"
                                    }}
                                    onClick={() => {
                                        setShowEstimate(false);
                                        setFileList([]);
                                        setImagePreview(null);
                                        setDescription("");
                                        form.resetFields();
                                    }}
                                >
                                    Try Another Item
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default EstimateListingPage;
